import { v4 as uuid4 } from 'uuid';
import moment from 'moment';
import { EmitterService } from '../emitter.service';
import { TransferStatus, listenerUploadStatusChanged } from './index';
import { Subject, Subscription } from 'rxjs';
import { TransferProgress } from './transfer-progress';
import { BasicFunction } from '../../models';

export enum TransferType {
  SignedURL = 'signedURL', //direct upload via signed url
  DirectUpload = 'directUpload', //direct upload to specific url
  TranscodingFailed = 'transcodingFailed', //direct upload, for file replacement due to transcoding issue
  Chunked = 'chunkedUpload', //upload via signed url, data is separated into multiple chunks
}

export interface MTMCustomFileExtraInfo {
  previewUrl?: string;
  remainingTime?: number;
  failedTranscodingInitUrl?: string;
  failedTranscodingFileId?: string;
  failedTranscodingReplacementType?: string;
  failedTranscodingForceCodec?: boolean;
  totalUploaded?: number;
}

export class MTMCustomFile {
  file: File;
  fileName: string = '';
  fileSize: number = 0.0;
  fileType: string = '';
  parentId?: string;

  /* ----------- * OVIRRADE * ----------- */
  name: string = '';
  size: number = 0.0;
  type: string = '';
  /* ----------- * OVIRRADE * ----------- */

  fileLocalId: string = '';
  localPlayPath: string = '';
  fileSizeMB: number = 0.0;
  dbFileObject: any = {};
  progress: number = 0.0;
  uploadStatusCode: string = TransferStatus.NEW;
  postUploadPayload: any = null; //payload to be sent during postUploadUrl, can be string which will be evaluated after preUpload
  postUploadAttributes: any;

  /* ----------- * - * ----------- */
  timeToStartUploading: number;
  /* ----------- * - * ----------- */

  refFileUploadWithSignedURL: any; //SignedURLFileUpload;

  /* ----------- * - * ----------- */
  createTime: number;

  //new props
  owner: string = '';
  pipelines: any[] = [];
  progress$: Subject<TransferProgress> = new Subject<TransferProgress>();
  step: string = '';
  asReference: boolean = false;
  preUploadUrl: string = '';
  postUploadUrl: string = '';
  sessionUrl: string = '';
  startIndex: number = 0;
  transferType: string = '';
  entity: any = {}; //associated entity info, contains at least id (and version if applicable)
  retryCount: number = 0;
  maxRetry: number = 3;
  currentRequest: Subscription = null;
  scanUpdateSubscription: Subscription = null;
  parentUrl: string = '';
  notificationId: string = '';
  iconUrl?: string = '';
  extraInfo: MTMCustomFileExtraInfo = {};
  extraHandlers: { [key: string]: BasicFunction } = {};
  lastProgressTime: number = 0;

  constructor(file: File, index: number) {
    this.file = file;
    if (file) {
      this.fileName = file.name;
      this.fileSize = file.size;
      this.fileType = file.type;
    }

    this.name = this.fileName;
    this.size = this.fileSize;
    this.type = this.fileType;

    this.uploadStatusCode = TransferStatus.NEW;
    this.fileLocalId = uuid4() + '_' + Date.now() + '_' + index;

    if (file) {
      this.localPlayPath = URL.createObjectURL(file);
      const sizeMB = parseInt(file.size.toFixed(2)) / (1024 * 1024);
      this.fileSizeMB = parseFloat(sizeMB.toFixed(2));
    }

    this.createTime = moment().valueOf();
  }

  //#region used by legacy code

  cancelUpload(that: this) {
    if (that.refFileUploadWithSignedURL?.isUploading) that.refFileUploadWithSignedURL.abourtUploading();

    that.setUploadStatusCode(TransferStatus.ERROR);
  }

  setProgress = (progress: number): void => {
    this.progress = progress;
    EmitterService.get('upload-file-progress-' + this.dbFileObject.sourceId).emit({
      file: this.file,
      progress: this.progress,
      dbFileObject: this.dbFileObject,
    });
  };

  public setUploadStatusCode_and_Progress(progress: number, uploadStatusCode: any) {
    this.setProgress(progress);
    this.uploadStatusCode = uploadStatusCode;
  }

  //#endregion

  public setDbFileObject(dbFileObject): void {
    this.dbFileObject = dbFileObject;
    if (typeof this.postUploadPayload == 'string') {
      this.postUploadPayload = JSON.parse(this.postUploadPayload.replace('%ID%', dbFileObject.id));
    }
  }

  public setUploadStatusCode(uploadStatusCode: string): void {
    this.uploadStatusCode = uploadStatusCode;
    listenerUploadStatusChanged.emit();
  }

  public setPostUploadAttributes(response): void {
    for (const [key, _] of Object.entries(this.postUploadAttributes)) {
      this.postUploadAttributes[key] = response[key];
    }
  }

  public setTimeToStartUploading() {
    this.timeToStartUploading = moment.now();
  }

  isResumableFromError(): boolean {
    return this.transferType === TransferType.Chunked;
  }

  cleanUp() {
    this.progress$.complete();
    if (this.currentRequest) {
      this.currentRequest.unsubscribe();
      this.currentRequest = null;
    }
    if (this.scanUpdateSubscription) {
      this.scanUpdateSubscription.unsubscribe();
      this.scanUpdateSubscription = null;
    }
  }

  static MakeMTMCustomFileAdapter(file: any) {
    if (!file.setUploadStatusCode) {
      file.setUploadStatusCode = (uploadStatusCode: string) => {
        file.uploadStatusCode = uploadStatusCode;
      };
    }

    if (!file.cleanUp && file.progress$) {
      file.cleanUp = () => {
        file.progress$.complete();
      };
    }

    if (!file.setDbFileObject) {
      file.setDbFileObject = (dbFileObject) => {
        file.dbFileObject = dbFileObject;
      };
    }

    if (!file.setTimeToStartUploading) {
      file.setTimeToStartUploading = () => (file.timeToStartUploading = moment.now());
    }
  }
}
